import { getAllCasesApi, searchWithCaseNumberApi } from 'Constants';
import { Context } from 'Context/Context';
import AdminCaseDetailsModal from 'Modals/AdminCaseDetailsModal';
import { getCaseType } from 'Utils';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Search, Download, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react';
import TimeframeHeader from './TimeframeHeader';

// Design 19d - admin "Cases list". Data, paging and date filter use the existing
// getAllCases API; filters + page are mirrored in the URL so a refresh or a
// shared link restores the same view.

const PAGE_SIZE = 20;
const DAY = 24 * 60 * 60 * 1000;
const GRID = '44px minmax(0,1.3fr) minmax(0,1.6fr) 110px 96px 72px 80px 72px';
// Newer case documents carry `createdAt`, older ones `created_at` - read either
const postedAt = (item) => item?.created_at || item?.createdAt;

// Click a header to sort the rows on this page (click again to reverse)
const HEADERS = [
  { label: '#', sortVal: (r) => r._idx },
  { label: 'Email', sortVal: (r) => (r.contactEmail || '').toLowerCase() },
  { label: 'Case type', sortVal: (r) => (getCaseType(r.caseType) || '').toLowerCase() },
  { label: 'Status', sortVal: (r) => statusOf(r).label },
  { label: 'Posted on', sortVal: (r) => new Date(postedAt(r) || 0).getTime() },
  { label: 'Requests', right: true, sortVal: (r) => r.requests || 0 },
  { label: 'Reviewing', right: true, sortVal: (r) => r.review || 0 },
  { label: 'Reported', right: true, sortVal: (r) => r.report || 0 },
];

const STATUS_STYLES = {
  deleted: { label: 'Deleted', color: '#9a3b2e', bg: 'rgba(154,59,46,.08)' },
  active: { label: 'Active', color: '#3a7d4f', bg: 'rgba(58,125,79,.1)' },
  closed: { label: 'Closed', color: '#14213d', bg: 'rgba(20,33,61,.06)' },
  pending: { label: 'Pending closing', color: 'rgba(20,33,61,.6)', bg: 'rgba(20,33,61,.05)' },
};
const statusOf = (item) =>
  item.is_deleted ? STATUS_STYLES.deleted
  : item.isClosed ? STATUS_STYLES.closed
  : item.isCasePendingClosing ? STATUS_STYLES.pending
  : STATUS_STYLES.active;

const FRAMES = ['all', 'today', 'week', 'month', 'custom'];
const rangeForFrame = (frame, from, to) => {
  const now = Date.now();
  if (frame === 'today') return { startDate: new Date(now), endDate: new Date(now) };
  if (frame === 'week') return { startDate: new Date(now - 7 * DAY), endDate: new Date(now) };
  if (frame === 'month') return { startDate: new Date(now - 30 * DAY), endDate: new Date(now) };
  if (frame === 'custom' && from && to) return { startDate: new Date(from), endDate: new Date(to) };
  return { startDate: null, endDate: null };
};

// Page list with "…" once there are more than 7 pages
const pageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('…');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('…');
  pages.push(total);
  return pages;
};

// SheetJS is only needed for the Excel export, so it's loaded on demand
const loadSheetJS = () => new Promise((resolve, reject) => {
  if (window.XLSX) return resolve(window.XLSX);
  const script = document.createElement('script');
  script.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
  script.onload = () => (window.XLSX ? resolve(window.XLSX) : reject(new Error('XLSX not available')));
  script.onerror = reject;
  document.body.appendChild(script);
});

const numStyle = (v) => ({
  font: "400 12.5px/1 'IBM Plex Mono',monospace",
  textAlign: 'right',
  color: v > 0 ? '#14213d' : 'rgba(20,33,61,.45)',
  fontWeight: v > 0 ? 500 : 400,
});

function AllCaseList({ viewSwitch }) {
    const { makeApiCallFunc } = useContext(Context);
    const [searchParams, setSearchParams] = useSearchParams();

    const urlFrame = FRAMES.includes(searchParams.get('posted')) ? searchParams.get('posted') : 'all';
    const initialRange = rangeForFrame(urlFrame, searchParams.get('from'), searchParams.get('to'));
    const initialFrame = (urlFrame === 'custom' && !initialRange.startDate) ? 'all' : urlFrame;
    const initialPage = Math.max(1, parseInt(searchParams.get('page')) || 1);

    const [casesList, setCasesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginatelimit, setPaginateLimit] = useState({ limit: PAGE_SIZE, skip: (initialPage - 1) * PAGE_SIZE });
    const [noEntries, setNoEntries] = useState(0);
    const [loadList, setLoadList] = useState(false);
    const [dateRange, setDateRange] = useState([{ ...initialRange, key: 'selection' }]);
    const [frame, setFrame] = useState(initialFrame);

    const [showCaseDetsModal, setShowCaseDetsModal] = useState(false);
    const [currentCaseId, setCurrentId] = useState(null);

    const [query, setQuery] = useState('');
    const [notFound, setNotFound] = useState(null);

    const [sort, setSort] = useState({ col: -1, dir: 'asc' });
    const toggleSort = (i) => setSort((prev) => prev.col === i ? { col: i, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col: i, dir: 'asc' });

    const [exportOpen, setExportOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    const dateFilter = () => {
      const startTime = dateRange[0]?.startDate ? dateRange[0].startDate : null;
      const endTime = dateRange[0]?.endDate ? dateRange[0].endDate : null;
      return startTime === null ? {} : { startTime, endTime };
    };

    useEffect(() => {
        window.scrollTo(0,0);
        setLoading(true);
        setSort({ col: -1, dir: 'asc' });
        makeApiCallFunc(
            getAllCasesApi,
            { ...dateFilter(), paginatelimit },
            ({data}) => {
              setCasesList(data?.cases || []);
              setNoEntries(parseInt(data?.noEntries) || 0);
              setLoading(false);
            },
            (data) => { console.log(data); setLoading(false); }
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[loadList, paginatelimit, dateRange]);

    // Mirror filters + page in the URL
    useEffect(() => {
      const p = { view: 'list' };
      const page = Math.floor(paginatelimit.skip / PAGE_SIZE) + 1;
      if (page > 1) p.page = String(page);
      if (frame !== 'all') {
        p.posted = frame;
        if (frame === 'custom' && dateRange[0]?.startDate && dateRange[0]?.endDate) {
          p.from = moment(dateRange[0].startDate).format('YYYY-MM-DD');
          p.to = moment(dateRange[0].endDate).format('YYYY-MM-DD');
        }
      }
      setSearchParams(p, { replace: true });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginatelimit, frame, dateRange]);

    const totalPages = Math.max(1, Math.ceil(noEntries / PAGE_SIZE));
    const currentPage = Math.floor(paginatelimit.skip / PAGE_SIZE) + 1;
    const goToPage = (p) => {
      if (p < 1 || p > totalPages || p === currentPage) return;
      setPaginateLimit((prev) => ({ ...prev, skip: (p - 1) * PAGE_SIZE }));
    };

    const clearFilters = () => {
      setFrame('all');
      setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
      setPaginateLimit({ limit: PAGE_SIZE, skip: 0 });
    };

    // Case ID search - exact match via the existing lookup; opens the case details
    const runSearch = () => {
      const q = query.trim();
      if (!q) return;
      makeApiCallFunc(searchWithCaseNumberApi, { caseNumber: q }, ({data}) => {
        if (!data || Object.keys(data).length === 0) {
          setNotFound(q);
        } else {
          setNotFound(null);
          setCurrentId(data?._id);
          setShowCaseDetsModal(true);
        }
      }, (error) => console.log(error));
    };

    // Export every case matching the current "Posted on" filter (not just this page)
    const fetchAllForExport = () => new Promise((resolve, reject) => {
      makeApiCallFunc(
        getAllCasesApi,
        { ...dateFilter(), paginatelimit: { skip: 0, limit: Math.max(noEntries, 1) } },
        ({data}) => resolve(data?.cases || []),
        (err) => reject(err)
      );
    });

    const buildRows = (cases) => [
      ['#', 'Case ID', 'Email', 'Case type', 'Status', 'Posted on', 'Requests', 'Reviewing', 'Reported'],
      ...cases.map((item, i) => [
        i + 1,
        item.caseNumber || '',
        item.contactEmail || '',
        getCaseType(item.caseType) || '',
        statusOf(item).label,
        postedAt(item) ? moment(postedAt(item)).format('MM/DD/YYYY') : '',
        item.requests || 0,
        item.review || 0,
        item.report || 0,
      ]),
    ];

    const fileBase = () => 'QCR_cases' + (frame !== 'all' ? '_' + frame : '') + '_' + moment().format('YYYY-MM-DD');

    const doExport = async (format) => {
      setExportOpen(false);
      setExporting(true);
      try {
        const rows = buildRows(await fetchAllForExport());
        if (format === 'csv') {
          const esc = (v) => {
            const s = String(v ?? '');
            return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
          };
          const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileBase() + '.csv';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        } else {
          const XLSX = await loadSheetJS();
          const ws = XLSX.utils.aoa_to_sheet(rows);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Cases');
          XLSX.writeFile(wb, fileBase() + '.xlsx');
        }
      } catch (e) {
        console.log(e);
        toast.error('Export failed. Please try again.');
      }
      setExporting(false);
    };

    // rows keep their global position (#) even when re-sorted
    const indexedRows = (casesList || []).map((r, i) => ({ ...r, _idx: paginatelimit.skip + i + 1 }));
    const rows = sort.col < 0 ? indexedRows : [...indexedRows].sort((a, b) => {
      const va = HEADERS[sort.col].sortVal(a);
      const vb = HEADERS[sort.col].sortVal(b);
      const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sort.dir === 'asc' ? cmp : -cmp;
    });

    const from = noEntries === 0 ? 0 : paginatelimit.skip + 1;
    const to = Math.min(paginatelimit.skip + PAGE_SIZE, noEntries);

  return (
    <div className='w-full font-plexsans'>
    {
      showCaseDetsModal &&
      <AdminCaseDetailsModal
      uploadNew={showCaseDetsModal}
      onClose={()=>setShowCaseDetsModal(false)}
      caseId={currentCaseId}
      />
    }

      {/* Toolbar: view switch + case ID search */}
      <div className='flex flex-wrap items-center justify-between' style={{ gap: '16px', marginBottom: notFound ? '10px' : '26px' }}>
        {viewSwitch}
        <div className='flex items-center bg-white' style={{ width: '280px', maxWidth: '100%', height: '36px', border: '1px solid rgba(20,33,61,.15)', borderRadius: '5px', padding: '0 12px', gap: '8px' }}>
          <Search size={14} strokeWidth={2} color="rgba(20,33,61,.45)" className='flex-none' />
          <input
            type='text'
            value={query}
            onChange={(e) => { setQuery(e.target.value); if (notFound) setNotFound(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            placeholder='Search by case ID'
            className='flex-1 min-w-0 bg-transparent outline-none text-navy placeholder:text-[rgba(20,33,61,0.4)]'
            style={{ fontSize: '13px' }}
          />
        </div>
      </div>
      {notFound &&
        <div className='flex justify-end' style={{ marginBottom: '16px', fontSize: '12.5px', color: 'rgba(20,33,61,.55)' }}>
          No case found with ID&nbsp;<span style={{ color: '#14213d', fontWeight: 500 }}>"{notFound}"</span>
        </div>
      }

      {/* Filter row */}
      <div className='flex flex-wrap items-center justify-between' style={{ gap: '12px', marginBottom: '16px' }}>
        <div className='flex flex-wrap items-center' style={{ gap: '10px' }}>
          <span style={{ font: "500 12.5px/1 'IBM Plex Sans',sans-serif", color: 'rgba(20,33,61,.6)' }}>Posted on</span>
          <TimeframeHeader
            className='flex items-center'
            showAll = {true}
            setLoadCaseAnalytics={setLoadList}
            frame={frame}
            setFrame={setFrame}
            setPaginateLimit={setPaginateLimit}
            setDateRange={setDateRange}
            dateRange={dateRange}/>
        </div>
        <div className='relative'>
          <button
            type='button'
            disabled={exporting}
            onClick={() => setExportOpen((o) => !o)}
            className='flex items-center transition-colors hover:bg-[rgba(138,98,49,0.06)] disabled:opacity-60'
            style={{ gap: '7px', border: '1.5px solid #8a6231', color: '#8a6231', font: "500 12.5px/1 'IBM Plex Sans',sans-serif", padding: '9px 14px', borderRadius: '5px', background: 'transparent' }}
          >
            <Download size={14} strokeWidth={2} />
            {exporting ? 'Exporting…' : 'Export'}
            <ChevronDown size={14} strokeWidth={2} />
          </button>
          {exportOpen &&
            <>
              <div className='fixed inset-0 z-30' onClick={() => setExportOpen(false)} />
              <div className='absolute right-0 z-40 bg-white overflow-hidden' style={{ top: '42px', minWidth: '170px', border: '1px solid rgba(20,33,61,.12)', borderRadius: '6px', boxShadow: '0 8px 24px rgba(20,33,61,.12)', padding: '4px 0' }}>
                {[{ key: 'csv', label: 'CSV' }, { key: 'xlsx', label: 'Excel (.xlsx)' }].map((o) => (
                  <div key={o.key} onClick={() => doExport(o.key)} className='cursor-pointer hover:bg-[rgba(138,98,49,0.06)]' style={{ padding: '10px 14px', fontSize: '13px', color: '#14213d' }}>
                    {o.label}
                  </div>
                ))}
              </div>
            </>
          }
        </div>
      </div>

      {/* Result count */}
      <div style={{ fontSize: '12.5px', color: 'rgba(20,33,61,.55)', marginBottom: '12px' }}>
        Showing <span style={{ color: '#14213d', fontWeight: 600 }}>{from}–{to}</span> of <span style={{ color: '#14213d', fontWeight: 600 }}>{noEntries}</span> cases
      </div>

      {/* Table */}
      <div className='bg-white' style={{ border: '1px solid rgba(20,33,61,.12)', borderRadius: '6px', overflow: 'hidden' }}>
        <div className='overflow-x-auto'>
          <div style={{ minWidth: '780px' }}>
            <div className='grid' style={{ gridTemplateColumns: GRID, background: '#14213d' }}>
              {HEADERS.map((h, i) => {
                const active = sort.col === i;
                const Icon = active ? (sort.dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
                return (
                  <div
                    key={h.label}
                    onClick={() => toggleSort(i)}
                    className={'flex items-center cursor-pointer select-none group ' + (h.right ? 'justify-end' : '')}
                    style={{ padding: '12px', gap: '4px', font: "600 10px/1.2 'IBM Plex Mono',monospace", textTransform: 'uppercase', letterSpacing: '.06em', color: active ? '#f7f4ed' : 'rgba(247,244,237,.75)' }}
                  >
                    <span>{h.label}</span>
                    <Icon size={11} strokeWidth={2.2} color={active ? '#c9a26a' : 'rgba(247,244,237,.35)'} className='flex-none' />
                  </div>
                );
              })}
            </div>

            {loading ?
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className='grid items-center' style={{ gridTemplateColumns: GRID, borderTop: '1px solid rgba(20,33,61,.07)', height: '44px', background: i % 2 ? '#fcfbf8' : '#fff' }}>
                  {HEADERS.map((h, j) => (
                    <div key={j} style={{ padding: '0 12px' }} className={h.right ? 'flex justify-end' : ''}>
                      <div className='animate-pulse rounded' style={{ height: '10px', width: j === 1 || j === 2 ? '70%' : '60%', background: 'rgba(20,33,61,.07)' }} />
                    </div>
                  ))}
                </div>
              ))
            : rows.length > 0 ?
              rows.map((item, key) => {
                const st = statusOf(item);
                return (
                  <div
                    key={item._id || key}
                    onClick={() => { setCurrentId(item._id); setShowCaseDetsModal(true); }}
                    className='grid items-center cursor-pointer transition-colors hover:!bg-[rgba(138,98,49,0.05)]'
                    style={{ gridTemplateColumns: GRID, borderTop: '1px solid rgba(20,33,61,.07)', background: key % 2 ? '#fcfbf8' : '#fff' }}
                  >
                    <div style={{ padding: '12px', font: "400 12px/1 'IBM Plex Mono',monospace", color: 'rgba(20,33,61,.45)' }}>{item._idx}</div>
                    <div className='truncate' style={{ padding: '12px', fontSize: '12.5px', color: item.contactEmail ? 'rgba(20,33,61,.6)' : 'rgba(20,33,61,.3)' }}>{item.contactEmail || '—'}</div>
                    <div className='truncate' style={{ padding: '12px', font: "500 13px/1.3 'IBM Plex Sans',sans-serif", color: '#14213d' }}>{getCaseType(item.caseType)}</div>
                    <div style={{ padding: '12px' }}>
                      <span className='inline-flex items-center whitespace-nowrap' style={{ gap: '5px', font: "600 11px/1 'IBM Plex Sans',sans-serif", padding: '5px 9px', borderRadius: '20px', color: st.color, background: st.bg }}>
                        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: st.color, display: 'inline-block' }} />
                        {st.label}
                      </span>
                    </div>
                    <div style={{ padding: '12px', fontSize: '12.5px', color: 'rgba(20,33,61,.7)' }}>{postedAt(item) ? moment(postedAt(item)).format("MM/DD/YYYY") : '—'}</div>
                    <div style={{ padding: '12px', ...numStyle(item.requests || 0) }}>{item.requests || 0}</div>
                    <div style={{ padding: '12px', ...numStyle(item.review || 0) }}>{item.review || 0}</div>
                    <div style={{ padding: '12px', ...numStyle(item.report || 0) }}>{item.report || 0}</div>
                  </div>
                );
              })
            :
              <div className='flex flex-col items-center justify-center text-center' style={{ borderTop: '1px solid rgba(20,33,61,.07)', padding: '48px 16px', gap: '8px' }}>
                <div style={{ fontSize: '13px', color: 'rgba(20,33,61,.5)' }}>No cases match these filters</div>
                {frame !== 'all' &&
                  <span onClick={clearFilters} className='cursor-pointer hover:text-tanDark' style={{ fontSize: '12.5px', fontWeight: 500, color: '#8a6231', textDecoration: 'underline' }}>Clear filters</span>
                }
              </div>
            }
          </div>
        </div>

        {/* Pagination */}
        {!loading && noEntries > 0 &&
          <div className='flex flex-wrap items-center justify-between bg-white' style={{ borderTop: '1px solid rgba(20,33,61,.1)', padding: '14px 16px', gap: '10px' }}>
            <button
              type='button'
              disabled={currentPage === 1}
              onClick={() => goToPage(currentPage - 1)}
              className='flex items-center transition-colors enabled:hover:bg-[rgba(20,33,61,0.03)] disabled:cursor-not-allowed'
              style={{ gap: '6px', border: '1px solid rgba(20,33,61,.12)', padding: '8px 14px', borderRadius: '4px', fontSize: '12.5px', fontWeight: 500, color: currentPage === 1 ? 'rgba(20,33,61,.35)' : '#14213d', background: '#fff' }}
            >
              <ChevronLeft size={14} strokeWidth={2} /> Previous
            </button>
            <div className='flex items-center flex-wrap justify-center' style={{ gap: '6px' }}>
              {pageList(currentPage, totalPages).map((p, i) => (
                p === '…' ?
                  <span key={'e' + i} style={{ width: '20px', textAlign: 'center', fontSize: '12.5px', color: 'rgba(20,33,61,.45)' }}>…</span>
                  :
                  <button
                    key={p}
                    type='button'
                    onClick={() => goToPage(p)}
                    className='flex items-center justify-center transition-colors'
                    style={p === currentPage
                      ? { width: '30px', height: '30px', borderRadius: '4px', background: '#14213d', color: '#fffdf8', fontSize: '12.5px', fontWeight: 600 }
                      : { width: '30px', height: '30px', borderRadius: '4px', border: '1px solid rgba(20,33,61,.15)', color: 'rgba(20,33,61,.6)', fontSize: '12.5px', background: '#fff' }}
                  >
                    {p}
                  </button>
              ))}
            </div>
            <button
              type='button'
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
              className='flex items-center transition-colors bg-tan enabled:hover:bg-tanDark disabled:opacity-40 disabled:cursor-not-allowed'
              style={{ gap: '6px', padding: '8px 14px', borderRadius: '4px', fontSize: '12.5px', fontWeight: 500, color: '#fffdf8' }}
            >
              Next <ChevronRight size={14} strokeWidth={2} />
            </button>
          </div>
        }
      </div>
    </div>
  )
}

export default AllCaseList
